import { List, Map } from 'immutable';

import { getActualDate, getDateDifference, isAfter } from './time';
import { keysToCamelCase } from './string';
import { sortCaseIgnore } from './array';
import { navigation } from './navigation';
import { isUnpaid } from './billing';
import { getSize } from './iterable/getSize';

import { CSV_STATUS, DAYS_TO_REFRESH, POLLING_INTERVAL, STATUSES, STATUS_TO_INDICATOR, STEP, SYNC_INDICATOR, SYNC_MODAL, SYNC_STEPS } from '../constants/shops';
import { CHANNEL_BY_ID, EBAY, FAIRE } from '../constants/channels';
import { BETA_CHANNELS, FEATURE } from '../constants/features';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { EMPTY, VELA } from '../constants';
import { STATUS } from '../constants/listings';
import { MODALS } from '../constants/modal';
import { PAGES } from '../constants/routes';

import actions from '../actions';

export function shapeShopsForApp({ response }) {
  function sortShopIds(x, y) {
    const shopX = response.byId[x];
    const shopY = response.byId[y];

    if (shopX.channel_id !== shopY.channel_id) {
      return sortCaseIgnore(CHANNEL_BY_ID[shopX.channel_id], CHANNEL_BY_ID[shopY.channel_id]);
    }

    return sortCaseIgnore(shopX.name, shopY.name);
  }

  function reduceShopIds(result, key) {
    const { channel_id: channelId, ...rest } = response.byId[key];
    const channel = CHANNEL_BY_ID[channelId];
    const shopId = String(key);

    switch (channel) {
      case EBAY: {
        if (!rest.marketplace_id) {
          rest.marketplace_id = 'EBAY_US';
        }

        break;
      }

      default: {
        break;
      }
    }

    result.options = result.options.push(shopId);
    result.byId = result.byId.set(shopId,
      keysToCamelCase({ data: rest, toImmutable: true })
        .set('channel', channel)
        .set('channelId', channelId)
    );

    return result;
  }

  return response.options.sort(sortShopIds).reduce(reduceShopIds, { byId: EMPTY.MAP, options: EMPTY.LIST });
}

export function isImporting(status) {
  return (
    !status ||
    status === STATUSES.INACTIVE_USER ||
    status === STATUSES.INITIAL_SYNC ||
    status === STATUSES.REFRESH_USER ||
    status === STATUSES.REIMPORT_SHOP
  );
}

export function isInvalid(status) {
  switch (status) {
    case STATUSES.EBAY_INVALID_MARKETPLACE:
    case STATUSES.INACTIVE_USER:
    case STATUSES.INCOMPLETE:
    case STATUSES.INCOMPLETE_DUPLICATE:
    case STATUSES.INCOMPLETE_TOO_MANY_LISTINGS:
    case STATUSES.NOT_FOUND:
    case STATUSES.SHOP_IMPORT_FAILURE:
    case STATUSES.UNAVAILABLE_SHOP:
    case STATUSES.UNKNOWN_ERROR: {
      return true;
    }

    default: {
      return false;
    }
  }
}

export function isValid(status) {
  switch (status) {
    case STATUSES.EBAY_INVALID_MARKETPLACE:
    case STATUSES.INACTIVE_USER:
    case STATUSES.INCOMPLETE:
    case STATUSES.INCOMPLETE_DUPLICATE:
    case STATUSES.INCOMPLETE_IN_VACATION_MODE:
    case STATUSES.INCOMPLETE_TOO_MANY_LISTINGS:
    case STATUSES.NOT_FOUND:
    case STATUSES.SHOP_IMPORT_FAILURE:
    case STATUSES.TOKEN_REJECTED:
    case STATUSES.UNAVAILABLE_FORBIDDEN:
    case STATUSES.UNAVAILABLE_LOCKED:
    case STATUSES.UNAVAILABLE_SHOP:
    case STATUSES.UNAVAILABLE_PAYMENT_REQUIRED:
    case STATUSES.UNKNOWN_ERROR: {
      return false;
    }

    default: {
      return true;
    }
  }
}

export function isEbayInvalidMarketplace(syncStatus) {
  return syncStatus === STATUSES.EBAY_INVALID_MARKETPLACE;
}

export function isSyncing(status) {
  switch (status) {
    case STATUSES.INCOMPLETE:
    case STATUSES.INCOMPLETE_DUPLICATE:
    case STATUSES.INCOMPLETE_IN_VACATION_MODE:
    case STATUSES.INCOMPLETE_TOO_MANY_LISTINGS:
    case STATUSES.QUOTA_EXCEEDED:
    case STATUSES.SHOP_IMPORT_FAILURE:
    case STATUSES.TOKEN_REJECTED:
    case STATUSES.UNAVAILABLE_FORBIDDEN:
    case STATUSES.UNAVAILABLE_LOCKED:
    case STATUSES.UNAVAILABLE_SHOP:
    case STATUSES.UNAVAILABLE_PAYMENT_REQUIRED:
    case STATUSES.UNKNOWN_ERROR:
    case STATUSES.UPLOAD_COMPLETE:
    case STATUSES.UP_TO_DATE: {
      return false;
    }

    default: {
      return true;
    }
  }
}

export function csvIsInProgress(status) {
  return status === CSV_STATUS.IN_PROGRESS;
}

export function isUpToDate(status) {
  return !isSyncing(status);
}

export function allShopsAreUpToDate(shopsById) {
  function reduceStatus(result, shop) {
    return result && isUpToDate(shop.get('syncStatus'));
  }

  return shopsById.reduce(reduceStatus, true);
}

export function getIntervalsFromConfig(state) {
  return {
    short: state.getIn(['user', 'config', 'shopsPollingIntervalShort'], POLLING_INTERVAL.SHORT),
    long: state.getIn(['user', 'config', 'shopsPollingIntervalLong'], POLLING_INTERVAL.LONG),
  };
}

export function getPollingInterval({ currentShopId, intervals, state }) {
  function getIntervalForShop(shopId) {
    return (
      isSyncing(state.getIn(['shops', 'byId', shopId, 'syncStatus'])) ||
      csvIsInProgress(state.getIn(['shops', 'importData', shopId, 'status']))
    )
      ? intervals.short
      : intervals.long;
  }

  function reduceShopIdsInEdit(result, shopId) {
    if (result === intervals.short) return result;

    return getIntervalForShop(shopId);
  }

  return getSize(state.getIn(['edit', 'shops']))
    ? state.getIn(['edit', 'shops']).reduce(reduceShopIdsInEdit, intervals.long)
    : getIntervalForShop(currentShopId);
}

export function shopSyncFinished({ current, last, shopId }) {
  if (!last.has(shopId)) return false;

  const currentState = current.getIn([shopId, 'syncStatus']);
  const lastState = last.getIn([shopId, 'syncStatus']);
  const currentTimestamp = current.getIn([shopId, 'lastSyncTimestamp']);
  const lastTimestamp = last.getIn([shopId, 'lastSyncTimestamp'], currentTimestamp);

  return (
    !isSyncing(currentState) && (
      isSyncing(lastState) ||
      isAfter(new Date(currentTimestamp), new Date(lastTimestamp))
    )
  );
}

export function shopSyncStarted({ current, last, shopId }) {
  const currentState = current.getIn([shopId, 'syncStatus']);
  const lastState = last.getIn([shopId, 'syncStatus']);
  return isSyncing(currentState) && (lastState === undefined || !isSyncing(lastState));
}

export function canShowProgress({ channel, syncStatus }) {
  switch (channel) {
    case FAIRE: {
      switch (syncStatus) {
        case STATUSES.ADMIN_SYNC:
        case STATUSES.INITIAL_SYNC:
        case STATUSES.USER_INITIATED_REFRESH:
        case STATUSES.ADMIN_REFRESH: {
          return false;
        }

        default: {
          return true;
        }
      }
    }

    default: {
      return true;
    }
  }
}

export function getShopSyncData({ oldData, shop, syncFinished, userFeatures }) {
  const channel = shop.get('channel');
  const syncStatus = shop.get('syncStatus');
  const publicErrorsEnabled = userFeatures.get(FEATURE.PUBLIC_ERRORS);
  const publicError = publicErrorsEnabled && shop.get('publicError');

  let step = shop.get('syncStatusStep');
  let modal = SYNC_MODAL[syncStatus];
  let indicator = !modal && STATUS_TO_INDICATOR[syncStatus];

  if (step === STEP.HIDE) {
    modal = undefined;
    indicator = SYNC_INDICATOR.HIDDEN;
    step = undefined;
  }

  if (syncStatus === STATUSES.APPLYING_OPERATIONS) {
    if (oldData.get('modal')) {
      if (
        oldData.get('modal') === MODALS.SYNC.PUBLISH ||
        oldData.get('modal') === MODALS.SYNC.SCHEDULED_PUBLISH ||
        oldData.get('modal') === MODALS.SYNC.SCHEDULED_UPDATE
      ) {
        indicator = undefined;
        modal = oldData.get('modal');
        step = undefined;
      }
    } else if (oldData.get('indicator')) {
      if (
        oldData.get('indicator') === SYNC_INDICATOR.SAVE ||
        oldData.get('indicator') === SYNC_INDICATOR.SYNC ||
        oldData.get('indicator') === SYNC_INDICATOR.PUBLISH
      ) {
        indicator = oldData.get('indicator');
        step = undefined;
      }
    }
  }

  function getCompleted() {
    let newData = oldData
      .set('complete', true)
      .set('processed', oldData.get('total'))
      .set('progress', 1)
      .set('started', true)
      .set('success', true);

    if (!oldData.get('step')) {
      let steps = List(SYNC_STEPS[channel][(oldData.get('indicator') || oldData.get('modal'))]);

      if (!oldData.get('indicator') && oldData.get('modal') === MODALS.SYNC.COPY) {
        steps = EMPTY.LIST;
      }

      newData = newData
        .set('step', steps.get(0))
        .set('steps', steps);
    } else {
      step = oldData.getIn(['steps', getSize(oldData.get('steps')) - 1]);

      if (oldData.get('step') !== step) {
        newData = newData.set('step', step);
      }
    }

    return newData;
  }

  if (indicator === SYNC_INDICATOR.HIDDEN) {
    if (
      ((oldData.get('indicator') && oldData.get('indicator') !== indicator) || oldData.get('modal')) &&
      !oldData.get('complete')
    ) {
      return getCompleted().set('next', Map({ indicator }));
    } else if (oldData.get('indicator') === indicator || !oldData.get('indicator') || oldData.get('complete')) {
      return Map({ indicator });
    }
  }

  if (!modal && !indicator && (oldData.get('indicator') || oldData.get('modal'))) {
    if (syncFinished && oldData.get('indicator') === SYNC_INDICATOR.HIDDEN) return undefined;

    if (!syncFinished || oldData.get('complete')) return oldData;

    return getCompleted();
  } else if (indicator && oldData.get('indicator') && indicator !== oldData.get('indicator')) {
    indicator = oldData.get('indicator');
  } else if (
    modal &&
    oldData.get('modal') &&
    modal !== oldData.get('modal') &&
    !oldData.get('hidden')
  ) {
    modal = oldData.get('modal');
  }

  function getMetaData() {
    return shop.get('processingMetadata', EMPTY.MAP).merge(oldData.get('metaData', EMPTY.MAP));
  }

  function getCounts({ areSame }) {
    const showProgress = canShowProgress({ channel, syncStatus });

    if (oldData.get('step') && oldData.get('step') !== step && !oldData.get('success')) {
      return {
        processed: oldData.get('total'),
        progress: 1,
        success: true,
        started: true,
        step: oldData.get('step'),
        steps: oldData.get('steps'),
        total: showProgress ? oldData.get('total') : undefined,
      };
    }

    let processed = Math.max(shop.get('productsProcessedCount') || 0, 0);
    let total = showProgress ? Math.max(shop.get('productsToProcessCount') || 0, 0) : undefined;

    if (areSame) {
      processed = Math.max(processed, oldData.get('processed') || 0);

      if (total) {
        total = Math.max(total, oldData.get('total') || 0);
      }
    }

    const progress = showProgress ? processed && total && (processed / total) : 0;
    const started = showProgress ? (!!progress || !!total) : !!processed;

    return { processed, progress, started, total };
  }

  if (indicator) {
    const steps = List(SYNC_STEPS[channel][indicator]);
    const areSame = (
      oldData.get('indicator') === indicator &&
      oldData.get('step') === step
    );

    return Map({
      metaData: getMetaData(),
      indicator,
      step,
      steps,
      publicError,
      ...getCounts({ areSame }),
    });
  }

  if (modal) {
    const metaData = getMetaData();
    let steps = List(SYNC_STEPS[channel][modal]);

    if (
      !userFeatures.get(FEATURE.AI_V2) &&
      modal === MODALS.SYNC.COPY
    ) {
      steps = EMPTY.LIST;
      step = undefined;
    }

    const areSame = (
      oldData.get('modal') === modal &&
      oldData.get('step') === step
    );

    return Map({
      metaData,
      modal,
      step,
      steps,
      publicError,
      ...getCounts({ areSame }),
    });
  }

  return undefined;
}

function getShopImportData({ oldData, shop }) {
  const importStatus = shop.getIn(['csvImport', 'status']);

  switch (importStatus) {
    case CSV_STATUS.IN_PROGRESS: {
      const processed = Math.max(shop.getIn(['csvImport', 'processed']) || 0, 0);
      const total = Math.max(shop.getIn(['csvImport', 'total']) || 0, processed);
      const progress = processed && total && (processed / total);
      const started = processed > 0;
      return Map({
        processed,
        progress,
        started,
        status: importStatus,
        total,
      });
    }

    default: {
      if (csvIsInProgress(oldData.get('status')) && !oldData.get('complete')) {
        return oldData
          .set('complete', true)
          .set('processed', oldData.get('total'))
          .set('progress', 1)
          .set('started', true);
      }

      return undefined;
    }
  }
}

export function updateState({ current, shopsById }) {
  return function reduceShopIds({ actions: actionsToDispatch, state: oldState }, shopId) {
    let state = oldState;
    const last = state.getIn(['shops', 'byId'], EMPTY.MAP);

    if (
      state.hasIn(['shops', 'hideRateLimitPage', shopId]) &&
      shopsById.getIn([shopId, 'syncStatus']) !== STATUSES.QUOTA_EXCEEDED
    ) {
      state = state.deleteIn(['shops', 'hideRateLimitPage', shopId]);
    }

    // If syncStatus is INACTIVE_USER it means user of this shop has not logged in for 90 days.
    if (shopsById.getIn([shopId, 'syncStatus']) === STATUSES.INACTIVE_USER) {
      if (!state.getIn(['shops', 'refreshing', shopId])) {
        actionsToDispatch.push(actions.shops.refreshUserShop(shopId));
      }
    } else if (state.getIn(['shops', 'refreshing', shopId])) {
      state = state.deleteIn(['shops', 'refreshing', shopId]);

      if (getSize(state.getIn(['shops', 'refreshing'])) === 0) {
        state = state.deleteIn(['shops', 'refreshing']);
      }
    }

    const { PAGE } = navigation({ state });
    const syncStarted = shopSyncStarted({ current: shopsById, last, shopId });
    const syncFinished = !syncStarted && shopSyncFinished({ current: shopsById, last, shopId });
    const importFinished = (
      csvIsInProgress(last.getIn([shopId, 'csvImport', 'status'])) &&
      !csvIsInProgress(shopsById.getIn([shopId, 'csvImport', 'status']))
    );

    if (syncFinished) {
      actionsToDispatch.push(
        actions.data.getShopData({ force: true, shopId }),
        actions.shops.getScheduled(shopId),
      );

      switch (PAGE) {
        case PAGES.LISTINGS: {
          if (
            state.getIn(['listings', 'shopId']) === shopId && (
              state.getIn(['shops', 'syncData', shopId, 'modal']) || (
                state.getIn(['shops', 'syncData', shopId, 'indicator']) &&
                state.getIn(['shops', 'syncData', shopId, 'indicator']) !== SYNC_INDICATOR.HIDDEN
              ) ||
              getSize(state.getIn(['listings', 'placeholders', shopId])) > 0 ||
              getSize(state.getIn(['listings', 'products', 'new', shopId])) > 0 ||
              getSize(state.getIn(['listings', 'products', 'processing', shopId])) > 0
            )
          ) {
            actionsToDispatch.push(
              actions.listings.getFilters(),
              actions.listings.getListings(),
              actions.listings.getStatuses(),
            );
          }

          break;
        }

        case PAGES.SCHEDULE_LISTINGS: {
          if (
            state.getIn(['schedule', 'listings', 'shopId']) === shopId && (
              state.getIn(['shops', 'syncData', shopId, 'modal']) || (
                state.getIn(['shops', 'syncData', shopId, 'indicator']) &&
                state.getIn(['shops', 'syncData', shopId, 'indicator']) !== SYNC_INDICATOR.HIDDEN
              ) ||
              getSize(state.getIn(['schedule', 'listings', 'products', 'processing'])) > 0
            )
          ) {
            actionsToDispatch.push(
              actions.schedule.getCounts(),
              actions.schedule.listings.getListings({ clear: true }),
            );
          }

          break;
        }

        case PAGES.SCHEDULE_UPDATES: {
          if (
            state.getIn(['schedule', 'updates', 'shopId']) === shopId &&
            state.getIn(['shops', 'syncData', shopId, 'modal']) === MODALS.SYNC.SCHEDULED_UPDATE
          ) {
            actionsToDispatch.push(
              actions.schedule.getCounts(),
              actions.schedule.updates.getUpdates({ clear: true }),
            );
          }

          break;
        }

        default: {
          break;
        }
      }
    }

    if (importFinished) {
      if (
        PAGE !== PAGES.LISTINGS ||
        state.getIn(['listings', 'shopId']) !== shopId || (
          state.getIn(['listings', 'status']) !== STATUS[VELA].IMPORTED &&
          state.getIn(['listings', 'status']) !== STATUS[VELA].UNPUBLISHED
        )
      ) {
        const success = shopsById.getIn([shopId, 'csvImport', 'status']) !== CSV_STATUS.FAILED;
        actionsToDispatch.push(
          actions.notifications.add({
            type: success ? NOTIFICATION.SUCCESS : NOTIFICATION.ERROR,
            message: success ? MESSAGE.SUCCESS.IMPORT_CSV : MESSAGE.FAIL.IMPORT_CSV,
          })
        );
      }

      if (PAGE === PAGES.LISTINGS && state.getIn(['listings', 'shopId']) === shopId) {
        actionsToDispatch.push(actions.listings.getStatuses());

        if (
          state.getIn(['listings', 'status']) === STATUS[VELA].IMPORTED ||
          state.getIn(['listings', 'status']) === STATUS[VELA].UNPUBLISHED
        ) {
          actionsToDispatch.push(
            actions.listings.getFilters(),
            actions.listings.getListings(),
          );
        }
      }
    }

    const shop = shopsById.get(shopId);
    const syncStatus = shop.get('syncStatus');
    const userFeatures = state.getIn(['user', 'features'], EMPTY.MAP);
    const syncData = getShopSyncData({
      oldData: state.getIn(['shops', 'syncData', shopId], EMPTY.MAP),
      shop,
      syncFinished,
      userFeatures,
    });

    state = syncData && (shopId === current || !syncData.get('complete'))
      ? state.setIn(['shops', 'syncData', shopId], syncData)
      : state.deleteIn(['shops', 'syncData', shopId]);

    const importData = getShopImportData({
      importFinished,
      oldData: state.getIn(['shops', 'importData', shopId], EMPTY.MAP),
      shop,
    });

    state = importData && (shopId === current || !importData.get('complete'))
      ? state.setIn(['shops', 'importData', shopId], importData)
      : state.deleteIn(['shops', 'importData', shopId]);

    if (
      !shop.get('isVelaOnlyShop') &&
      (syncFinished || !state.hasIn(['shops', 'refreshRequired', shopId])) &&
      !isInvalid(syncStatus) &&
      !isImporting(syncStatus) &&
      !(
        syncStatus === STATUSES.TOKEN_REJECTED ||
        syncStatus === STATUSES.INCOMPLETE_IN_VACATION_MODE ||
        (
          syncStatus === STATUSES.QUOTA_EXCEEDED &&
          !state.getIn(['shops', 'hideRateLimitPage', shopId])
        )
      )
    ) {
      const syncTimestamp = shop.get('lastSuccessfulSyncAt') || shop.get('lastSyncTimestamp');
      const actualDate = getActualDate();
      const lastSyncDate = new Date(syncTimestamp);
      const { days } = getDateDifference(actualDate, lastSyncDate, false, false);

      if (days >= DAYS_TO_REFRESH) {
        if (!state.getIn(['shops', 'refreshRequired', shopId, 'modal'])) {
          state = state.setIn(['shops', 'refreshRequired', shopId],
            Map({ daysFromSync: days, modal: true })
          );
        }
      } else {
        state = state.setIn(['shops', 'refreshRequired', shopId], Map({ modal: false }));
      }
    }

    return { actions: actionsToDispatch, state };
  };
}

export function getTopShopId({ shops, user }) {

  for (const shopId of shops.get('options').values()) {
    const channel = shops.getIn(['byId', shopId, 'channel']);
    if (
      shops.hasIn(['byId', shopId]) &&
      !shops.getIn(['byId', shopId, 'isVelaOnlyShop']) &&
      isValid(shops.getIn(['byId', shopId, 'syncStatus'])) && (
        BETA_CHANNELS.has(channel) ||
        !isUnpaid(user.getIn(['subscriptions', shopId]))
      )
    ) {
      return shopId;
    }
  }

  for (const shopId of shops.get('options').values()) {
    if (shops.getIn(['byId', shopId, 'isVelaOnlyShop'])) {
      return shopId;
    }
  }

  return shops.getIn(['options', 0]);
}
